import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Check, Lock, Share2 } from 'lucide-react';
import { SelfTestNotice } from './SelfTestNotice';
import { useCompleteAssessment } from '../hooks/useTakeAssessment';
import { AccountHandoffActions } from './flowScreens';
import type { ShareStatus } from '../api/takeApi';

const GREEN = 'bg-[#026857] hover:bg-[#025247]';

type CompletionScreenProps = {
  token: string;
  assessmentId: string;
  advisorName: string;
  clientEmail: string;
  /** The advisor is taking this themselves — see SelfTestNotice. */
  isSelfTest: boolean;
  /** Whether this email already has an account — picks sign-in vs create path (Feature 095 split). */
  hasAccount: boolean;
  /** Starts account creation (route layer wires this to the guest signup modal). */
  onCreateAccount: (email: string) => void;
  /** Starts normal sign-in for an existing account. */
  onSignIn: () => void;
};

/**
 * Sharing choice + account handoff (Feature 095, Slice 3). The client picks
 * whether to share results, which completes + scores the assessment; then account
 * creation starts (the route layer owns the signup modal). The link token is
 * stashed so the new account claims (re-owns) the assessment on first sign-in.
 *
 * On the advisor's own test run (Feature 118) both halves addressed someone the
 * advisor is not: the choice asked them to share with themselves, by name, and
 * the handoff offered to create an account they are already signed into. Mirrors
 * V2CompletionScreen, which carries the longer note.
 */
export function CompletionScreen({
  token,
  assessmentId,
  advisorName,
  clientEmail,
  isSelfTest,
  hasAccount,
  onCreateAccount,
  onSignIn,
}: CompletionScreenProps) {
  const navigate = useNavigate();
  const complete = useCompleteAssessment(token);
  const [chosen, setChosen] = useState<ShareStatus | null>(null);

  // A client reads their advisor's name here; an advisor reading their own would
  // be asked to share with themselves.
  const advisor = isSelfTest ? 'your advisor' : advisorName;

  const choose = (shareStatus: ShareStatus) => {
    setChosen(shareStatus);
    complete.mutate(shareStatus);
  };

  if (complete.isSuccess) {
    return (
      <div className="mx-auto w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#026857] text-white">
          <Check className="h-6 w-6" />
        </div>
        <h2 className="font-serif text-2xl text-gray-900">
          {isSelfTest ? 'That’s the end of the client’s flow' : 'Your answers are saved'}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
          {isSelfTest ? (
            <>
              A client would create their account here to see their report. Your test run is saved
              and stays out of your stats and client list — and your report is open to you
              whichever choice you just made.
            </>
          ) : (
            <>
              {chosen === 'SHARED'
                ? `You chose to share your results with ${advisorName}.`
                : `Your results stay private. You can choose to share with ${advisorName} later.`}{' '}
              {hasAccount
                ? 'Sign in to view your full report.'
                : 'Create your account to view your full report.'}
            </>
          )}
        </p>
        {isSelfTest ? (
          <Button
            type="button"
            className={`mt-6 ${GREEN}`}
            onClick={() => navigate(`/longevity-assessment/results/${assessmentId}`)}
          >
            View your report
          </Button>
        ) : (
          // Feature 095 split: existing accounts lead with sign-in, new ones
          // with account creation; the handoff stashes the claim token itself.
          <AccountHandoffActions
            token={token}
            clientEmail={clientEmail}
            hasAccount={hasAccount}
            onCreateAccount={onCreateAccount}
            onSignIn={onSignIn}
          />
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      {isSelfTest && <SelfTestNotice />}
      <h2 className="font-serif text-2xl text-gray-900">You&apos;ve completed the assessment</h2>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
        Would you like to share your results with {advisor}?
      </p>

      {complete.isError && (
        <p className="mt-3 text-sm text-red-600">Something went wrong. Please try again.</p>
      )}

      <div className="mt-6 space-y-3 text-left">
        <button
          type="button"
          disabled={complete.isPending}
          onClick={() => choose('SHARED')}
          className="flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-[#026857] disabled:opacity-60"
        >
          <Share2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#026857]" />
          <span>
            <span className="block font-medium text-gray-900">Share my results with {advisor}</span>
            <span className="block text-sm text-gray-500">
              Your advisor sees your full report — score, all six pillars, and next steps.
            </span>
          </span>
        </button>

        <button
          type="button"
          disabled={complete.isPending}
          onClick={() => choose('NONE')}
          className="flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-[#026857] disabled:opacity-60"
        >
          <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" />
          <span>
            <span className="block font-medium text-gray-900">Don&apos;t share with {advisor}</span>
            <span className="block text-sm text-gray-500">
              They will only see that you completed it — not your score. You can share later.
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}
